import React, { useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Check, Upload, FileSpreadsheet, LogIn, LogOut, X } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, DateAvailability, DateAvailabilityMap, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { DateAvailabilityModal } from './DateAvailabilityModal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, upsertContributorRecord, deleteContributorRecord, reorderContributorRecords, upsertSolutionRecord, deleteSolutionRecord, reorderSolutionRecords, addDateAvailabilityRecord, deleteDateAvailabilityRecord, fetchInitialStateFromDatabase } from '../utils/database';
import { normalizeHydratedSolutions, reindexSolutions } from '../utils/solutionOrder';
import { normalizeHydratedContributors, reindexContributors } from '../utils/contributorOrder';
function stableSlugId(input: string) {
  const normalized = input.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) {
    return slug;
  }
  let hash = 0;
  for (const char of normalized || input.trim().toLowerCase()) {
    hash = hash * 31 + char.codePointAt(0)! >>> 0;
  }
  return `id-${hash.toString(16)}`;
}
const STORAGE_KEYS = {
  CONTRIBUTORS: 'availability-dashboard-contributors',
  SOLUTIONS: 'availability-dashboard-solutions',
  AVAILABILITY: 'availability-dashboard-availability',
  DATE_AVAILABILITY: 'availability-dashboard-date-availability',
  EXPERIENCE_LEVELS: 'availability-dashboard-experience-levels'
};
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'admin';
  const hasDashboardDataAccess = isSignedIn && accessLevel !== 'unregistered' && accessLevel !== 'anonymous';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [dateAvailability, setDateAvailability] = useState<DateAvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [isHydrating, setIsHydrating] = useState(true);
  const hasInitializedContributors = useRef(false);
  const shouldSkipNextSave = useRef(true);
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      hasInitializedContributors.current = false;
      shouldSkipNextSave.current = true;
      try {
        Object.values(STORAGE_KEYS).forEach(k => {
          sessionStorage.removeItem(k);
          localStorage.removeItem(k);
        });
      } catch {
        /* ignore */
      }
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setDateAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    if (isSignedIn && !hasDashboardDataAccess) {
      hasInitializedContributors.current = false;
      shouldSkipNextSave.current = true;
      try {
        Object.values(STORAGE_KEYS).forEach(k => {
          sessionStorage.removeItem(k);
          localStorage.removeItem(k);
        });
      } catch {
        /* ignore */
      }
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setDateAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      if (isCancelled) {
        return;
      }

      // Security: never fall back to sessionStorage or buildInitialState() when db-get-state
      // fails or returns 403. Client-spoofed accessLevel could otherwise load demo/cached data
      // without a successful server-authorized read.
      if (!databaseState) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setDateAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
        return;
      }
      setContributors(normalizeHydratedContributors(databaseState.contributors));
      setSolutions(normalizeHydratedSolutions(databaseState.solutions));
      setAvailability(databaseState.availability);
      setDateAvailability(databaseState.dateAvailability || {});
      setExperienceLevels(databaseState.experienceLevels);
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn, accessLevel]);
  useEffect(() => {
    if (isHydrating || !hasDashboardDataAccess) {
      return;
    }
    if (!hasInitializedContributors.current) {
      hasInitializedContributors.current = true;
      const today = new Date().toISOString().split('T')[0];
      let needsUpdate = false;
      const updatedContributors = contributors.map(c => {
        if (c.oooStartDate && c.oooEndDate) {
          const shouldBeOOO = c.oooStartDate <= today && c.oooEndDate >= today;
          if (c.isOutOfOffice !== shouldBeOOO) {
            needsUpdate = true;
            return {
              ...c,
              isOutOfOffice: shouldBeOOO
            };
          }
        } else if (c.isOutOfOffice && !c.oooStartDate) {

          // Legacy: if isOutOfOffice is true but no dates, leave as-is (manual toggle)
        }
        return c;
      });
      if (needsUpdate) {
        setContributors(updatedContributors);
        return;
      }
    }
    if (shouldSkipNextSave.current) {
      shouldSkipNextSave.current = false;
      return;
    }
    sessionStorage.setItem(STORAGE_KEYS.CONTRIBUTORS, JSON.stringify(contributors));
    sessionStorage.setItem(STORAGE_KEYS.SOLUTIONS, JSON.stringify(solutions));
    sessionStorage.setItem(STORAGE_KEYS.AVAILABILITY, JSON.stringify(availability));
    sessionStorage.setItem(STORAGE_KEYS.DATE_AVAILABILITY, JSON.stringify(dateAvailability));
    sessionStorage.setItem(STORAGE_KEYS.EXPERIENCE_LEVELS, JSON.stringify(experienceLevels));
    setLastSaved(new Date());
    setShowSavedIndicator(true);
    const timer = setTimeout(() => setShowSavedIndicator(false), 2000);
    return () => clearTimeout(timer);
  }, [contributors, solutions, availability, dateAvailability, experienceLevels, isHydrating, hasDashboardDataAccess]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICEmail, setNewICEmail] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newICNameError, setNewICNameError] = useState('');
  const [newICEmailError, setNewICEmailError] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionNameError, setNewSolutionNameError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editSolutionName, setEditSolutionName] = useState('');
  const [scheduleTarget, setScheduleTarget] = useState<{
    contributorId: string;
    solutionId: string;
  } | null>(null);
  // OOO modal state
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooStartDate, setOOOStartDate] = useState('');
  const [oooEndDate, setOOOEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsScheduleModalOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = reindexSolutions(solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        })));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            email: '',
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  const saveContributorRecord = (contributor: Contributor) => {
    void upsertContributorRecord(contributor);
  };
  const saveSolutionOrder = (orderedSolutions: Solution[]) => {
    if (orderedSolutions.length === 0) {
      return;
    }
    void reorderSolutionRecords(orderedSolutions.map(solution => ({
      id: solution.id,
      displayOrder: solution.displayOrder
    })));
  };
  const saveContributorOrder = (orderedContributors: Contributor[]) => {
    if (orderedContributors.length === 0) {
      return;
    }
    void reorderContributorRecords(orderedContributors.map(contributor => ({
      id: contributor.id,
      displayOrder: contributor.displayOrder
    })));
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorAvailability = availability[contributorId] || {};
    const currentStatus = contributorAvailability[solutionId] || 'unavailable';
    const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
      unavailable: 'limited',
      limited: 'available',
      available: 'unavailable'
    };
    const newStatus = statusCycle[currentStatus];
    setAvailability(prev => {
      const nextContributorAvailability = prev[contributorId] || {};
      return {
        ...prev,
        [contributorId]: {
          ...nextContributorAvailability,
          [solutionId]: newStatus
        }
      };
    });
    if (contributor && solution) {
      void logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]);
    }
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorLevels = experienceLevels[contributorId] || {};
    const currentLevel = contributorLevels[solutionId];
    const cycle: Record<string, ExperienceLevel | undefined> = {
      undefined: 'L1',
      L1: 'L2',
      L2: 'L3',
      L3: 'L4',
      L4: undefined as any
    };
    const newLevel = cycle[String(currentLevel)];
    setExperienceLevels(prev => {
      const nextContributorLevels = {
        ...(prev[contributorId] || {})
      };
      if (newLevel) {
        nextContributorLevels[solutionId] = newLevel;
      } else {
        delete nextContributorLevels[solutionId];
      }
      return {
        ...prev,
        [contributorId]: nextContributorLevels
      };
    });
    if (contributor && solution) {
      const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
      void logExperienceLevelChange(contributor, solution, newLevel, currentStatus);
    }
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    const updatedContributor = contributor ? {
      ...contributor,
      region,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
  };
  const handleAddContributor = async () => {
    if (!requestAdminAccess()) return;
    let hasError = false;
    if (!newICName.trim()) {
      setNewICNameError('Name is required.');
      hasError = true;
    } else {
      setNewICNameError('');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newICEmail.trim()) {
      setNewICEmailError('Email is required.');
      hasError = true;
    } else if (!emailRegex.test(newICEmail.trim())) {
      setNewICEmailError('Enter a valid email address.');
      hasError = true;
    } else {
      setNewICEmailError('');
    }
    if (hasError) return;
    const contributorName = newICName.trim();
    const contributorId = stableSlugId(contributorName);
    if (!contributorId) {
      setNewICNameError('Unable to generate a valid ID for that name.');
      return;
    }
    if (contributors.some(contributor => contributor.id === contributorId)) {
      setNewICNameError('A contributor with that name already exists.');
      return;
    }
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: contributorId,
      name: contributorName,
      email: newICEmail.trim(),
      region: newICRegion,
      lastUpdated: new Date().toISOString()
    };
    const nextContributors = reindexContributors([...contributors, newContributor]);
    const createdContributor = nextContributors.find(c => c.id === newContributor.id) || nextContributors[nextContributors.length - 1];
    const response = await upsertContributorRecord(createdContributor);
    if (!response.success) {
      alert('Failed to save the contributor. Please try again.');
      return;
    }
    setContributors(nextContributors);
    setNewICName('');
    setNewICEmail('');
    setNewICRegion('USA');
    setNewICNameError('');
    setNewICEmailError('');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) {
      setNewSolutionNameError('Solution name is required.');
      return;
    }
    const solutionName = newSolutionName.trim();
    if (solutions.some(s => s.name.toLowerCase() === solutionName.toLowerCase())) {
      setNewSolutionNameError('A solution with that name already exists.');
      return;
    }
    setNewSolutionNameError('');
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(solutionName),
      name: solutionName
    };
    const nextSolutions = reindexSolutions([...solutions, newSolution]);
    const createdSolution = nextSolutions.find(solution => solution.id === newSolution.id) || nextSolutions[nextSolutions.length - 1];
    setSolutions(nextSolutions);
    if (createdSolution) {
      void upsertSolutionRecord(createdSolution);
    }
    setNewSolutionName('');
    setNewSolutionNameError('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      const contributor = contributors.find(c => c.id === id);
      const nextContributors = reindexContributors(contributors.filter(c => c.id !== id));
      setContributors(nextContributors);
      setAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setExperienceLevels(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setDateAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      if (contributor) {
        await deleteContributorRecord({
          id: contributor.id,
          name: contributor.name,
          region: contributor.region
        });
      }
      saveContributorOrder(nextContributors);
    }
  };
  const handleDeleteSolution = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      const solution = solutions.find(s => s.id === id);
      const nextSolutions = reindexSolutions(solutions.filter(s => s.id !== id));
      setSolutions(nextSolutions);
      setAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setExperienceLevels(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setDateAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      if (solution) {
        await deleteSolutionRecord({
          id: solution.id,
          name: solution.name
        });
      }
      saveSolutionOrder(nextSolutions);
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setSelectedContributorId(selectedContributorId === contributorId ? null : contributorId);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    const reorderedContributors = reindexContributors(newContributors);
    setContributors(reorderedContributors);
    saveContributorOrder(reorderedContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    const reorderedSolutions = reindexSolutions(newSolutions);
    setSolutions(reorderedSolutions);
    saveSolutionOrder(reorderedSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestAdminAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = () => {
    if (!requestAdminAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    const updatedContributor: Contributor = {
      ...editingContributor,
      name: editContributorName.trim(),
      region: editContributorRegion,
      lastUpdated: new Date().toISOString()
    };
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? updatedContributor : c));
    saveContributorRecord(updatedContributor);
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestAdminAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = () => {
    if (!requestAdminAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    const nextSolutions = reindexSolutions(solutions.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    const updatedSolution = nextSolutions.find(s => s.id === editingSolution.id);
    setSolutions(nextSolutions);
    if (updatedSolution) {
      void upsertSolutionRecord(updatedSolution);
    }
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // OOO handler - now opens modal instead of toggling
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    setOOOTargetContributorId(contributorId);
    setOOOStartDate(contributor?.oooStartDate || '');
    setOOOEndDate(contributor?.oooEndDate || '');
    setOOOError('');
    setIsOOOModalOpen(true);
  };
  const handleSaveOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    // If both dates are empty, clear OOO
    if (!oooStartDate && !oooEndDate) {
      const updatedContributor = contributor ? {
        ...contributor,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: new Date().toISOString()
      } : null;
      setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
        ...c,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
      } : c));
      if (updatedContributor) {
        saveContributorRecord(updatedContributor);
      }
      setIsOOOModalOpen(false);
      return;
    }
    if (!oooStartDate || !oooEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    const start = new Date(oooStartDate);
    const end = new Date(oooEndDate);
    if (end < start) {
      setOOOError('End date must be on or after start date');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const isCurrentlyOOO = oooStartDate <= today && oooEndDate >= today;
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  const handleClearOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  // Date Availability Handlers
  const handleOpenSchedule = (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    setScheduleTarget({
      contributorId,
      solutionId
    });
    setIsScheduleModalOpen(true);
  };
  const handleAddSchedule = (schedule: Omit<DateAvailability, 'id'>) => {
    if (!requestManagementAccess()) return;
    if (!scheduleTarget) return;
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const newSchedule: DateAvailability = {
      ...schedule,
      id: stableSlugId(`${contributorId}-${solutionId}-${schedule.startDate}-${schedule.endDate}-${schedule.status}-${Date.now()}`)
    };
    if (contributor && solution) {
      void addDateAvailabilityRecord({
        ...newSchedule,
        contributorId,
        solutionId
      }, contributor, solution);
    }
    setDateAvailability(prev => {
      const contributorSchedules = prev[contributorId] || {};
      const currentSchedules = contributorSchedules[solutionId] || [];
      return {
        ...prev,
        [contributorId]: {
          ...contributorSchedules,
          [solutionId]: [...currentSchedules, newSchedule]
        }
      };
    });
    touchContributor(contributorId);
  };
  const handleDeleteSchedule = (scheduleId: string) => {
    if (!requestManagementAccess()) return;
    if (!scheduleTarget) return;
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    // Find the schedule being deleted to log its details
    const scheduleToDelete = dateAvailability[contributorId]?.[solutionId]?.find(s => s.id === scheduleId);
    if (contributor && solution && scheduleToDelete) {
      void deleteDateAvailabilityRecord({
        ...scheduleToDelete,
        contributorId,
        solutionId
      }, contributor, solution);
    }
    setDateAvailability(prev => {
      const contributorSchedules = prev[contributorId] || {};
      const currentSchedules = contributorSchedules[solutionId] || [];
      return {
        ...prev,
        [contributorId]: {
          ...contributorSchedules,
          [solutionId]: currentSchedules.filter(s => s.id !== scheduleId)
        }
      };
    });
    touchContributor(contributorId);
  };
  // Helper to get current schedules for the modal
  const getCurrentSchedules = () => {
    if (!scheduleTarget) return [];
    const {
      contributorId,
      solutionId
    } = scheduleTarget;
    return dateAvailability[contributorId]?.[solutionId] || [];
  };
  // Helper to get names for the modal
  const getScheduleNames = () => {
    if (!scheduleTarget) return {
      contributorName: '',
      solutionName: ''
    };
    const contributor = contributors.find(c => c.id === scheduleTarget.contributorId);
    const solution = solutions.find(s => s.id === scheduleTarget.solutionId);
    return {
      contributorName: contributor?.name || '',
      solutionName: solution?.name || ''
    };
  };
  // Calculate effective availability for display
  // This merges base availability with date-based overrides for TODAY
  const getEffectiveAvailability = () => {
    const effective = {
      ...availability
    };
    const today = new Date().toISOString().split('T')[0];
    Object.entries(dateAvailability).forEach(([contributorId, solutionMap]) => {
      Object.entries(solutionMap).forEach(([solutionId, schedules]) => {
        const activeSchedule = schedules.find(s => s.startDate <= today && s.endDate >= today);
        if (activeSchedule) {
          if (!effective[contributorId]) effective[contributorId] = {};
          effective[contributorId][solutionId] = activeSchedule.status;
        }
      });
    });
    return effective;
  };
  const effectiveAvailability = getEffectiveAvailability();
  const accessLevelLabel = accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : accessLevel === 'unregistered' ? 'Unregistered' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-52">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-53">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-54">
          <div className="flex items-center gap-4" data-id="element-55">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-56" />
            <div data-id="element-57">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-58">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-59">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-60">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-61">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-62" /> <br data-id="element-63" />
              <p data-id="element-64">
              1. Click Sign-In <br data-id="element-65" />
              2. Login using your company account <br data-id="element-66" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-67">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-68">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-69" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-70">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-71">
          <div className="flex items-center gap-4" data-id="element-72">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-73" />
            <div data-id="element-74">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-75">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-76">
                Account not provisioned
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-77">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-78">
              You are signed in with Adobe ID, but this account is not registered for dashboard access.
              No resource data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-79">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="outline" size="lg" isLoading={isAuthLoading} data-id="element-80">
              
              <LogOut className="mr-2 h-4 w-4" data-id="element-81" />
              Sign out
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-82">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-83">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-84">
          <div className="flex items-center gap-3" data-id="element-85">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-86" />
            
            <div data-id="element-87">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-88">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-89">
                <p className="text-sm text-slate-500" data-id="element-90">
                  Track solution coverage and individual capacity
                </p>
                {showSavedIndicator && <div className="flex items-center gap-1 text-xs text-green-600 animate-in fade-in duration-200" data-id="element-91">
                    <Check className="h-3 w-3" data-id="element-92" />
                    <span data-id="element-93">Saved</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-94">
            <div className="relative hidden md:block" data-id="element-95">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-96" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-97" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-98" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-99">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-100" />
                <span className="font-medium text-slate-800" data-id="element-101">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-102">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-103">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-104">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-105" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-106" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-107">
                
                  <Download className="h-4 w-4 mr-2" data-id="element-108" />
                  Export Data
                </Button>
                {/* <Button
                 onClick={() => setIsImportOpen(true)}
                 variant="outline"
                 size="sm">
                                   <Upload className="h-4 w-4 mr-2" />
                  Import
                 </Button> */}
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-109">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-110" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-111">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-112" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        <div className="flex justify-end" data-id="element-113">
          <span className="text-xs hover:text-blue-500" data-id="element-114"><a target="_blank" href="https://adobe-my.sharepoint.com/:x:/r/personal/dsagar_adobe_com/_layouts/15/doc2.aspx?sourcedoc=%7B430e4f3f-47f7-428f-9fb3-2c6f5f88ee7d%7D&action=edit" data-id="element-115">Feedback</a></span><span className="text-xs ml-1 italic" data-id="element-116">v1.0</span>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-117">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-118" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-119" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-120">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-121">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-122">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && accessLevel === 'viewer' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-123">
                Signed in as{' '}
                <span className="font-medium" data-id="element-124">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-125">
                Signed in as{' '}
                <span className="font-medium" data-id="element-126">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-127">
        <div className="flex flex-col gap-2" data-id="element-128">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-129">
            <div className="flex items-center gap-4 text-xs" data-id="element-130">
              <span className="font-medium text-slate-600" data-id="element-131">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-132">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-133"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-134">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-135">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-136"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-137">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-138">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-139"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-140">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-141" />

              <span className="font-medium text-slate-600" data-id="element-142">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-143">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-144">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-145">
                  
                  Foundational
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-146">
                  FOUNDATIONAL

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-147">Enablement Requirements:</p>
                  <p data-id="element-148">Intro-level enablement completed with understanding of the basics and value of the solution.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-149">Tooling Expectations:</p>
                  <p data-id="element-150">Learners can do a standard demo in a clickable and shared environment.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-151">Field Support Implications:</p>
                  <p data-id="element-152">Learners are apprenticing in the field and can support 1:Many demos.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-153">Outcome:</p>
                  <p data-id="element-154">Learners should be able to deliver a value pitch and 101 level demo.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-155">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-156">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-157">
                  
                  Intermediate
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-158">
                  INTERMEDIATE

                  <p className="font-bold mt-2" data-id="element-159">Enablement Requirements:</p>
                  <p data-id="element-160">Deeper dives into features, customer use Requests, and common integrations.</p>

                  <p className="font-bold mt-2" data-id="element-161">Tooling Expectations:</p>
                  <p data-id="element-162">Learners can do an ad hoc demo in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-163">Field Support Implications:</p>
                  <p data-id="element-164">Learners are supporting early-stage or low complexity single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-165">Outcome:</p>
                  <p data-id="element-166">Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-167">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-168">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-169">
                  
                  Advanced
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-170">
                  ADVANCED

                  <p className="font-bold mt-2" data-id="element-171">Enablement Requirements:</p>
                  <p data-id="element-172">Covers integrations with other solutions, data migration architectures, and expert-level guidance.</p>

                  <p className="font-bold mt-2" data-id="element-173">Tooling Expectations:</p>
                  <p data-id="element-174">Learners can demo product integrations and add-ons in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-175">Field Support Implications:</p>
                  <p data-id="element-176">Learners are supporting all single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-177">Outcome:</p>
                  <p data-id="element-178">Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-179">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-180">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-181">
                  
                  Champion
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-182">
                  CHAMPION

                  <p className="font-bold mt-2" data-id="element-183">Field Support Implications:</p>
                  <p data-id="element-184">Learners can support architecture and multi-solution conversations. The most complex meetings.</p>

                  <p className="font-bold mt-2" data-id="element-185">Outcome:</p>
                  <p data-id="element-186">Learners are the SME on the team for an individual product and a thought leader as the product evolves.</p>
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-187" />

              <span className="font-medium text-slate-600" data-id="element-188">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-189">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-190" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-191">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-192">
              <span className="text-xs font-medium text-slate-600" data-id="element-193">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-194">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-195">
                
                    <X className="h-3 w-3" data-id="element-196" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-197">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-198">
                
                    <X className="h-3 w-3" data-id="element-199" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-200">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-201">
                
                    <X className="h-3 w-3" data-id="element-202" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-203">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-204">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-205">
                
                    <X className="h-3 w-3" data-id="element-206" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-207">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-208">
                
                    <X className="h-3 w-3" data-id="element-209" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-210">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-211">
        <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={effectiveAvailability} dateAvailability={dateAvailability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} canAdministerEntities={isAdmin} canDeleteEntities={isAdmin} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} onOpenSchedule={handleOpenSchedule} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} data-id="element-212" />
        
      </main>

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-214">
            <div className="flex gap-2" data-id="element-215">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-216">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-217" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-218">
              
                <Download className="h-4 w-4 mr-2" data-id="element-219" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-220">
              Close
            </Button>
          </div>} data-id="element-213">
        
        <div className="space-y-4" data-id="element-221">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-222">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-223">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-224">
              <li data-id="element-225">
                First column: <strong data-id="element-226">Contributor</strong> (name)
              </li>
              <li data-id="element-227">
                Second column: <strong data-id="element-228">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-229">Remaining columns: Solution names</li>
              <li data-id="element-230">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-231">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-232">status:level</code>
              </li>
              <li data-id="element-233">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-234">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-235">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-236">unavailable</code>
              </li>
              <li data-id="element-237">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-238">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-239">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-240">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-241">L4</code> (Champion)
              </li>
              <li data-id="element-242">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-243">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-244">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-245">unavailable</code>
              </li>
              <li data-id="element-246">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-247">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-248">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-249" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-250">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-251" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-252">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-253">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-254">
            <strong data-id="element-255">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => {
      setIsAddICOpen(false);
      setNewICNameError('');
      setNewICEmailError('');
    }} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-257">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-258">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-259">Add Contributor</Button>
          </div>} data-id="element-256">
        
        <div className="space-y-4" data-id="element-260">
          <p className="text-sm text-slate-500" data-id="element-261">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => {
          setNewICName(e.target.value);
          setNewICNameError('');
        }} error={newICNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-262" />

          <Input label="Email" placeholder="e.g. jane.doe@adobe.com" type="email" value={newICEmail} onChange={e => {
          setNewICEmail(e.target.value);
          setNewICEmailError('');
        }} error={newICEmailError} onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-263" />

          <div data-id="element-264">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-265">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-266">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-267">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => {
      setIsAddSolutionOpen(false);
      setNewSolutionNameError('');
    }} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-269">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-270">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-271">Add Solution</Button>
          </div>} data-id="element-268">
        
        <div className="space-y-4" data-id="element-272">
          <p className="text-sm text-slate-500" data-id="element-273">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => {
          setNewSolutionName(e.target.value);
          setNewSolutionNameError('');
        }} error={newSolutionNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-274" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-276">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-277">
            
              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-278">Save Changes</Button>
          </div>} data-id="element-275">
        
        <div className="space-y-4" data-id="element-279">
          <p className="text-sm text-slate-500" data-id="element-280">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-281" />
          
          <div data-id="element-282">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-283">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-284">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-285">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-287">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-288">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-289">Save Changes</Button>
          </div>} data-id="element-286">
        
        <div className="space-y-4" data-id="element-290">
          <p className="text-sm text-slate-500" data-id="element-291">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-292" />
          
        </div>
      </Modal>

      {/* OOO Date Range Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Set Out of Office" footer={<div className="flex justify-between w-full" data-id="element-294">
            {oooStartDate || oooEndDate ? <Button variant="danger" size="sm" onClick={handleClearOOO} data-id="element-295">
                Clear OOO
              </Button> : <div data-id="element-296" />}
            <div className="flex gap-3" data-id="element-297">
              <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-298">
                Cancel
              </Button>
              <Button onClick={handleSaveOOO} data-id="element-299">Save</Button>
            </div>
          </div>} data-id="element-293">
        
        <div className="space-y-4" data-id="element-300">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-301">
                <p className="text-sm text-slate-600" data-id="element-302">
                  Setting Out of Office for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-303">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-304">
            Select a date range for when you'll be out of office. Your entire
            column will appear dimmed during this period.
          </p>

          <div className="grid grid-cols-2 gap-4" data-id="element-305">
            <Input type="date" label="Start Date" value={oooStartDate} onChange={e => setOOOStartDate(e.target.value)} data-id="element-306" />
            
            <Input type="date" label="End Date" value={oooEndDate} onChange={e => setOOOEndDate(e.target.value)} min={oooStartDate || undefined} data-id="element-307" />
            
          </div>

          {oooError && <p className="text-sm text-red-600" data-id="element-308">{oooError}</p>}

          {oooStartDate && oooEndDate && (() => {
          const today = new Date().toISOString().split('T')[0];
          const isActive = oooStartDate <= today && oooEndDate >= today;
          const isFuture = oooStartDate > today;
          return <div className={`p-3 rounded-md border text-sm ${isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : isFuture ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-309">
                
                  {isActive && '⚡ OOO is currently active'}
                  {isFuture && `📅 OOO scheduled: ${new Date(oooStartDate).toLocaleDateString()} – ${new Date(oooEndDate).toLocaleDateString()}`}
                  {!isActive && !isFuture && 'This date range is in the past'}
                </div>;
        })()}
        </div>
      </Modal>

      {/* Date Availability Modal */}
      {scheduleTarget && <DateAvailabilityModal isOpen={isScheduleModalOpen} onClose={() => setIsScheduleModalOpen(false)} contributorName={getScheduleNames().contributorName} solutionName={getScheduleNames().solutionName} schedules={getCurrentSchedules()} onAddSchedule={handleAddSchedule} onDeleteSchedule={handleDeleteSchedule} data-id="element-310" />}
    </div>;
}